import React, { useState } from 'react';

import { DndContext, type DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSnackbar } from 'notistack';

import AddIcon from '@mui/icons-material/Add';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  LinearProgress,
  Link,
  Paper,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { DataGrid, type GridColDef, type GridRowSelectionModel } from '@mui/x-data-grid';

import { useCopyPackageInclusionsOrder } from '~/hooks/apis/inclusions/useCopyPackageInclusionsOrder';
import { useUpdatePackageInclusions } from '~/hooks/apis/inclusions/useUpdatePackageInclusions';

import getErrorMessage from '~/utils/getErrorMessage';
import { reportError } from '~/utils/reportError';

import AddInclusionDialog from '../../../Inclusions/AddInclusionDialog';
import type { InclusionAssetEditValue } from '../../../Inclusions/InclusionAssetEditDialog';
import {
  PACKAGE_OVERRIDE_SORT_ORDER_BASE,
  type PackageInclusionRecord,
  getInclusionPersistId,
  isInclusionHidden,
} from '../../../utils';
import {
  type PackageInclusionsFieldName,
  getPackageInclusions,
  getPackageInclusionsFieldName,
  getPackageName,
} from '../../utils';

import PackageInclusionsTabRow from './PackageInclusionsTabRow';
import { PACKAGE_INCLUSIONS_GRID_COLUMNS } from './utils';

interface Props {
  offerId?: string;
  offerPackage: App.Package;
  onOpenInclusions: () => void;
  packages: Array<App.Package>;
  vendorId?: string;
  onChange: (nextPackageValues: Partial<App.Package> & Record<string, unknown>) => void;
}

const PACKAGE_COLUMNS: Array<GridColDef<CopyTargetPackageRow>> = [
  {
    disableColumnMenu: true,
    field: 'name',
    headerName: 'Package',
    flex: 2,
    minWidth: 240,
    display: 'flex',
  },
  {
    disableColumnMenu: true,
    field: 'id',
    filterable: false,
    headerName: 'Package ID',
    sortable: false,
    flex: 1,
    minWidth: 180,
    display: 'flex',
  },
];

interface CopyTargetPackageRow {
  id: string;
  name: string;
}

function isInclusionRecord(value: unknown): value is PackageInclusionRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normaliseInclusion(value: unknown): PackageInclusionRecord {
  if (isInclusionRecord(value)) {
    return value;
  }

  if (typeof value === 'string') {
    return { name: value };
  }

  return {};
}

function getInclusionRowId(inclusion: PackageInclusionRecord, index: number) {
  const recordId = inclusion.id ?? inclusion.inclusion_id ?? inclusion.inclusion?.id;

  return recordId ? `inclusion:${recordId}:${index}` : `inclusion-index:${index}`;
}

function getPackageCopyOrderId(offerPackage: App.Package) {
  return offerPackage.id_salesforce_external ? String(offerPackage.id_salesforce_external) : undefined;
}

function getPackageOpportunityName(inclusions: Array<PackageInclusionRecord>) {
  const opportunityName = inclusions.find((inclusion) => inclusion.opportunity_name)?.opportunity_name;

  return opportunityName ? String(opportunityName) : undefined;
}

export default function PackageInclusionsTab({
  offerId,
  offerPackage,
  onOpenInclusions,
  packages,
  vendorId,
  onChange,
}: Props) {
  const [showHidden, setShowHidden] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [selectedPackageIds, setSelectedPackageIds] = useState<GridRowSelectionModel>([]);
  const copyPackageInclusionsOrder = useCopyPackageInclusionsOrder();
  const updatePackageInclusions = useUpdatePackageInclusions();
  const { enqueueSnackbar } = useSnackbar();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const currentPackageId = getPackageCopyOrderId(offerPackage);
  const inclusionsFieldName: PackageInclusionsFieldName = getPackageInclusionsFieldName(offerPackage);
  const inclusions = getPackageInclusions(offerPackage).map(normaliseInclusion);
  const opportunityName = getPackageOpportunityName(inclusions);
  const inclusionRows = inclusions.map((inclusion, index) => ({
    inclusion,
    rowId: getInclusionRowId(inclusion, index),
  }));
  const visibleRows = showHidden
    ? inclusionRows
    : inclusionRows.filter(({ inclusion }) => !isInclusionHidden(inclusion));
  const visibleRowIds = visibleRows.map(({ rowId }) => rowId);
  const copyTargetPackageRows = packages.flatMap((packageOption) => {
    const packageId = getPackageCopyOrderId(packageOption);

    if (!packageId || packageId === currentPackageId) {
      return [];
    }

    return [
      {
        id: packageId,
        name: getPackageName(packageOption),
      },
    ];
  });
  const canCopyInclusionsOrder =
    !!offerId && !!vendorId && !!currentPackageId && inclusions.length > 0 && copyTargetPackageRows.length > 0;
  const showInclusionsValidityTag = (offerPackage as App.AccommodationPackage).show_inclusions_validity_tag !== false;

  const updateInclusions = (nextInclusions: Array<PackageInclusionRecord>) => {
    onChange({ [inclusionsFieldName]: nextInclusions });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldVisibleIndex = visibleRowIds.indexOf(String(active.id));
    const newVisibleIndex = visibleRowIds.indexOf(String(over.id));

    if (oldVisibleIndex === -1 || newVisibleIndex === -1) {
      return;
    }

    const reorderedVisibleRows = arrayMove(visibleRows, oldVisibleIndex, newVisibleIndex);
    const nextVisibleRows = [...reorderedVisibleRows];
    const nextInclusions = inclusionRows.map((row) => {
      if (showHidden || !isInclusionHidden(row.inclusion)) {
        return nextVisibleRows.shift()?.inclusion ?? row.inclusion;
      }

      return row.inclusion;
    });

    updateInclusions(
      nextInclusions.map((inclusion, index) => ({
        ...inclusion,
        sort_order: PACKAGE_OVERRIDE_SORT_ORDER_BASE + index + 1,
      })),
    );
  };

  const handleRowChange = (rowId: string, nextInclusion: PackageInclusionRecord, options?: { moveToTop?: boolean }) => {
    const nextInclusions = inclusions.map((inclusion, index) =>
      getInclusionRowId(inclusion, index) === rowId ? nextInclusion : inclusion,
    );

    if (!options?.moveToTop) {
      updateInclusions(nextInclusions);
      return;
    }

    const changedIndex = nextInclusions.findIndex((inclusion, index) => getInclusionRowId(inclusion, index) === rowId);
    const changedInclusion = nextInclusions[changedIndex];

    if (!changedInclusion) {
      updateInclusions(nextInclusions);
      return;
    }

    updateInclusions(
      [changedInclusion, ...nextInclusions.filter((_inclusion, index) => index !== changedIndex)].map(
        (inclusion, index) => ({ ...inclusion, sort_order: PACKAGE_OVERRIDE_SORT_ORDER_BASE + index + 1 }),
      ),
    );
  };

  const handleRowUnassign = (rowId: string) => {
    updateInclusions(inclusions.filter((inclusion, index) => getInclusionRowId(inclusion, index) !== rowId));
  };

  const handleCopyDialogClose = () => {
    if (!copyPackageInclusionsOrder.isPending) {
      setCopyDialogOpen(false);
      setSelectedPackageIds([]);
    }
  };

  const handleAddCustomInclusion = async (inclusion: InclusionAssetEditValue) => {
    if (!offerId || !vendorId || !currentPackageId) {
      enqueueSnackbar('Unable to add inclusion for this package.', { variant: 'warning' });
      throw new Error('Missing offer, vendor or package ID.');
    }

    try {
      await updatePackageInclusions.mutateAsync({
        offerId,
        packageInclusions: [
          {
            category_id: inclusion.categoryId ?? null,
            currency: inclusion.currency,
            is_hidden: inclusion.isHidden,
            name: inclusion.name,
            offer_id: offerId,
            opportunity_name: opportunityName,
            package_id: currentPackageId,
            package_name: getPackageName(offerPackage),
            requires_booking: inclusion.requiresBooking,
            rrp: inclusion.rrp,
            sort_order: PACKAGE_OVERRIDE_SORT_ORDER_BASE + inclusions.length + 1,
            style: null,
            lux_plus_tier: null,
            type: inclusion.type,
            unit: inclusion.unit,
            vendor_salesforce_id: vendorId,
          },
        ],
      });
      enqueueSnackbar('Custom inclusion added', { variant: 'success' });
    } catch (error) {
      reportError(error);
      enqueueSnackbar(getErrorMessage(error, 'Failed to add inclusion'), { variant: 'error' });
      throw error;
    }
  };

  const handleCopyInclusionsOrder = async () => {
    if (!offerId || !vendorId || !currentPackageId) {
      enqueueSnackbar('Unable to copy inclusions order for this package.', { variant: 'warning' });
      return;
    }

    const packageInclusions = inclusions.flatMap((inclusion, index) => {
      const inclusionId = getInclusionPersistId(inclusion);

      return inclusionId
        ? [
            {
              inclusion_id: inclusionId,
              sort_order: PACKAGE_OVERRIDE_SORT_ORDER_BASE + index + 1,
            },
          ]
        : [];
    });

    if (packageInclusions.length === 0) {
      enqueueSnackbar('No saved inclusions are available to copy.', { variant: 'warning' });
      return;
    }

    try {
      await copyPackageInclusionsOrder.mutateAsync({
        offerId,
        vendorId,
        packageIds: Array.from(new Set(selectedPackageIds.concat(currentPackageId).map(String))),
        packageInclusions,
      });
      enqueueSnackbar('Copy inclusions order success', { variant: 'success' });
      setCopyDialogOpen(false);
      setSelectedPackageIds([]);
    } catch (error) {
      enqueueSnackbar(`Failed to copy inclusions order to packages: ${(error as Error).message}`, { variant: 'error' });
    }
  };

  return (
    <Stack spacing={3}>
      <Alert severity="info">
        Ordering should be managed in the{' '}
        <Link component="button" type="button" fontWeight={700} onClick={onOpenInclusions}>
          Inclusions tab
        </Link>
        . This per-package list is kept for manual overrides only.
      </Alert>

      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={showInclusionsValidityTag}
              onChange={(event) => onChange({ show_inclusions_validity_tag: event.target.checked })}
            />
          }
          label="Show inclusions validity tag"
          sx={{ m: 0 }}
        />
        <FormHelperText sx={{ ml: 0, mt: 0.5 }}>
          Displays the &quot;Inclusions valid for X&quot; note below this package&apos;s inclusions when structured
          policy data is available.
        </FormHelperText>
      </Box>

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Box sx={{ overflowX: 'auto' }}>
          <Box
            sx={{
              bgcolor: 'grey.50',
              display: 'grid',
              gap: 2,
              gridTemplateColumns: PACKAGE_INCLUSIONS_GRID_COLUMNS,
              px: 2,
              py: 1.5,
            }}
          >
            <Box />
            <Typography variant="overline" sx={{ fontWeight: 700 }}>
              Category
            </Typography>
            <Typography variant="overline" sx={{ fontWeight: 700 }}>
              Inclusion
            </Typography>
            <Typography variant="overline" align="center" sx={{ fontWeight: 700 }}>
              Highlight
            </Typography>
            <Typography variant="overline" align="center" sx={{ fontWeight: 700 }}>
              LuxPlus only
            </Typography>
            <Typography variant="overline" align="center" sx={{ fontWeight: 700 }}>
              Requires booking
            </Typography>
            <Box />
          </Box>

          {visibleRows.length > 0 ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={visibleRowIds} strategy={verticalListSortingStrategy}>
                {visibleRows.map(({ inclusion, rowId }) => (
                  <PackageInclusionsTabRow
                    key={rowId}
                    id={rowId}
                    inclusion={inclusion}
                    offerId={offerId}
                    packageId={currentPackageId}
                    packageName={getPackageName(offerPackage)}
                    vendorId={vendorId}
                    onChange={(nextInclusion, options) => handleRowChange(rowId, nextInclusion, options)}
                    onUnassign={() => handleRowUnassign(rowId)}
                  />
                ))}
              </SortableContext>
            </DndContext>
          ) : (
            <Box sx={{ borderTop: 1, borderColor: 'divider', px: 2, py: 3 }}>
              <Typography variant="body2" color="text.secondary">
                No inclusions added.
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} useFlexGap flexWrap="wrap">
        <Button
          variant="text"
          startIcon={<AddIcon />}
          disabled={!offerId || !vendorId || !currentPackageId || updatePackageInclusions.isPending}
          onClick={() => setAddDialogOpen(true)}
        >
          Create custom inclusion
        </Button>
        <Button variant="text" onClick={() => setShowHidden((currentValue) => !currentValue)}>
          {showHidden ? 'Hide hidden' : 'Show hidden'}
        </Button>
        <Button
          variant="text"
          href={vendorId ? `/vendors/${vendorId}/inclusions` : undefined}
          target="_blank"
          rel="noreferrer"
          disabled={!vendorId}
        >
          Vendor inclusions
        </Button>
        <Button variant="text" disabled={!canCopyInclusionsOrder} onClick={() => setCopyDialogOpen(true)}>
          Copy inclusions ordering
        </Button>
      </Stack>

      <Dialog open={copyDialogOpen} onClose={handleCopyDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>Select packages to copy inclusions order to</DialogTitle>
        <Box sx={{ position: 'relative' }}>
          {copyPackageInclusionsOrder.isPending && (
            <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, width: '100%' }} />
          )}
        </Box>
        <DialogContent dividers>
          <DataGrid
            autoHeight
            checkboxSelection
            columns={PACKAGE_COLUMNS}
            density="compact"
            disableRowSelectionOnClick
            rows={copyTargetPackageRows}
            hideFooter
            onRowSelectionModelChange={setSelectedPackageIds}
            rowSelectionModel={selectedPackageIds}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={copyPackageInclusionsOrder.isPending} onClick={handleCopyDialogClose}>
            Close
          </Button>
          <Button
            disabled={selectedPackageIds.length === 0 || copyPackageInclusionsOrder.isPending}
            variant="contained"
            onClick={handleCopyInclusionsOrder}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <AddInclusionDialog
        isSaving={updatePackageInclusions.isPending}
        open={addDialogOpen}
        packageOptions={currentPackageId ? [{ id: currentPackageId, name: getPackageName(offerPackage) }] : []}
        onAdd={handleAddCustomInclusion}
        onClose={() => setAddDialogOpen(false)}
      />
    </Stack>
  );
}
